/**
 * Social Studio Store — brand character, onboarding, research, and sprints.
 */
import { create } from 'zustand';
import type { ResearchResults } from '@/hooks/useSocialResearchSSE';
import type { BrandConfig } from '@/config/brand';

// ── Types ───────────────────────────────────────────────────────────────────

export interface BrandCharacter {
  artifactId: string;
  name: string;
  /** Primary front-facing portrait. Used for thumbnails / display. */
  portraitUrl: string;
  /** Round-3 multi-angle pack — ¾ left, ¾ right, back. Generated via
   *  POST /core/content-studio/social-character/angle-pack. When present,
   *  every Kling dispatch ships these as additional identity anchors,
   *  lifting reference fidelity from ~50% to ~90%. Empty / undefined for
   *  legacy characters; dispatch falls back gracefully. */
  additionalPortraitUrls?: string[];
  storageKey?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface BrandIdentityData {
  brandName: string;
  industry: string;
  platforms: string[];
  goal: string;
  website?: string;
  audience?: string;
  inspirationBrands?: string[];
  vibeChips?: string[];
}

export interface CharacterGenJob {
  jobId: string;
  sessionId: string;
  count: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  portraits?: Array<{ imageUrl: string }>;
  error?: string;
}

export interface BriefScript {
  hook: string;
  body: Array<{ timestamp: string; visual: string; audio: string; text_overlay?: string }>;
  cta: string;
}

export type UgcHookType =
  | 'discovery'
  | 'problem-solution'
  | 'aesthetic-lifestyle'
  | 'grwm'
  | 'honest-review'
  | 'humor'
  | 'identity'
  | 'beauty-closeup'
  | 'pov'
  | 'dependency'
  | 'shock'
  | 'before-after';

export type StoryboardBeatType = 'HOOK' | 'SETUP' | 'REVEAL' | 'PAYOFF';
export type StoryboardProductPresence = 'none' | 'entering' | 'hero' | 'background';

export interface StoryboardBeat {
  beatType: StoryboardBeatType;
  timecode: string;            // e.g. "[0:00-0:03]"
  spokenLine: string;
  visualDescription: string;
  background: string;
  creatorAction: string;
  cameraDirection: string;
  productPresence: StoryboardProductPresence;
}

export interface Storyboard {
  angle: string;
  hookType: UgcHookType;
  targetProblem: string;
  estimatedDuration: '10s' | '15s' | '30s';
  assignedAvatarId?: string;
  beats: StoryboardBeat[];     // exactly 4 — HOOK / SETUP / REVEAL / PAYOFF
}

export type VideoEngine = 'kling-multishot' | 'seedance';

/**
 * Older briefs were generated with the now-defunct `veo` and `kling-i2v`
 * engines. On hydration we fold those values to a sensible default so the
 * UI keeps rendering instead of crashing on an unknown enum.
 */
export function normalizeVideoEngine(raw: unknown): VideoEngine {
  if (raw === 'kling-multishot') return 'kling-multishot';
  if (raw === 'seedance') return 'seedance';
  // 'veo' / 'kling-i2v' / undefined / anything else → safe default.
  return 'kling-multishot';
}

export interface EngineHint {
  preferred: VideoEngine;
  cfg_scale: number;            // 0..1
  negative_prompt: string;
}

export interface VideoBrief {
  id?: string;
  calendar_slot: string;
  template_id: string;
  title: string;
  platform: string;
  duration: string;
  script: BriefScript;
  production_notes: {
    filming_style: string;
    equipment_needed: string;
    estimated_production_time: string;
    can_automate: boolean;
    automation_pipeline?: 'cinematic' | 'ugc' | 'manual';
  };
  trending_elements: string[];
  video_premise?: string;
  emotional_beat?: string;

  /* Phase 1 — additive storyboard / engine fields. All optional so old briefs keep working. */
  hook_type?: UgcHookType;
  storyboard?: Storyboard;
  engine_hint?: EngineHint;
  /** N scene-reference stills, one per shot, in shot order. Length matches `shot_count` (default 4). */
  scene_ref_urls?: string[];
  /** Shot count for this brief (1-6). Default 4 (one per HOOK/SETUP/REVEAL/PAYOFF beat).
   *  Single-shot archetypes use 1; narrative archetypes use 4-6. */
  shot_count?: number;

  /* Phase 2 — brief archetype + reference URLs. */
  brief_archetype?: 'narrative-multishot' | 'single-shot-cinematic' | 'transformation' | 'talking-head' | 'aesthetic-tableau';
  end_image_url?: string;
  motion_reference_video_url?: string;
  audio_reference_url?: string;
  palette_swatch_url?: string;
  storyboard_composite_image_url?: string;

  /* Phase 4 — 5-section motion prompt (the lean Seedance signal).
   *
   * When present, the dispatcher uses these sections verbatim instead of
   * building a prompt from the 4-beat storyboard. Pattern from WaveSpeedAI
   * + fal.ai field-tested templates: Subject / Action / Camera / Style /
   * Constraints, ~150 words total, drop the word "dynamic" entirely. */
  motion_prompt?: {
    subject: string;
    action: string;
    camera: string;
    style: string;
    constraints: string;
  };
  /** Composite storyboard panel count. Default 9 (3×3) for narrative-multishot. */
  storyboard_panel_count?: number;
  /** Composite storyboard layout. Derived from panel_count by default. */
  storyboard_layout?: '3x3' | '2x3' | '2x2' | 'single';
}

export interface GeneratedVideo {
  id: string;
  briefId?: string;
  title: string;
  assetUrl: string;
  createdAt: string;
}

export interface Sprint {
  id: string;
  label: string;
  createdAt: string;
  startDate?: string;
  briefs: VideoBrief[];
  generatedVideos: GeneratedVideo[];
}

export type ResearchStatus = 'idle' | 'running' | 'complete' | 'error';

// ── Store ───────────────────────────────────────────────────────────────────

interface SocialStudioState {
  workspaceId: string | null;

  /** Currently selected canonical Brand DNA ID (null = no brand picked).
   *  Mirrors the user-wide active brand from `/brands/active/`. The picker
   *  at the top of SocialStudioWorkspace mutates this; the in-flow brief /
   *  research / calendar paths read `selectedBrandConfig` so they ship full
   *  brand context (palette, voice, audience) to downstream pipelines. */
  selectedBrandId: string | null;
  /** Cached full BrandConfig for the selected brand — populated lazily by
   *  the SocialStudioWorkspace effect whenever `selectedBrandId` changes.
   *  When present, `buildBrandInput` derives `SocialBrandInput` from this
   *  rather than the legacy ephemeral `brandIdentity` form data. */
  selectedBrandConfig: BrandConfig | null;

  character: BrandCharacter | null;
  characterLoading: boolean;
  characterError: string | null;
  /** True while the multi-angle pack (¾L / ¾R / back portraits) is
   *  generating in the background after a character lock. Used by
   *  CharacterSetupInline's success card and CharacterTab's
   *  AnglePackPanel to surface progress without each having to wire its
   *  own loading state. */
  characterAnglePackGenerating: boolean;
  /** Last error from a multi-angle pack generation attempt. Null when
   *  the pack succeeded or hasn't been attempted. Cleared when a new
   *  attempt starts. Surfaced by AnglePackPanel + the wizard phase
   *  card so the user sees a real reason instead of silent failure. */
  characterAnglePackError: string | null;

  brandIdentity: Partial<BrandIdentityData>;

  characterGenJob: CharacterGenJob | null;
  selectedPortraitIndex: number | null;

  sprints: Sprint[];

  // Research persistence (decoupled from sprint ceremony)
  researchResults: ResearchResults | null;
  researchStatus: ResearchStatus;
  researchError: string | null;
  lastResearchAt: string | null;

  // Actions
  setWorkspaceId: (id: string) => void;
  /** Select a brand by ID (null clears). Clears cached config so the
   *  workspace's lazy-fetch effect repopulates it on the next change. */
  setSelectedBrandId: (brandId: string | null) => void;
  /** Cache the full BrandConfig for the selected brand. */
  setSelectedBrandConfig: (config: BrandConfig | null) => void;
  setCharacter: (c: BrandCharacter | null) => void;
  setCharacterLoading: (l: boolean) => void;
  setCharacterError: (e: string | null) => void;
  setCharacterAnglePackGenerating: (g: boolean) => void;
  setCharacterAnglePackError: (e: string | null) => void;
  updateBrandIdentity: (partial: Partial<BrandIdentityData>) => void;
  setCharacterGenJob: (j: CharacterGenJob | null) => void;
  setSelectedPortraitIndex: (i: number | null) => void;
  addSprint: (s: Sprint) => void;
  renameSprint: (sprintId: string, label: string) => void;
  deleteSprint: (sprintId: string) => void;
  addGeneratedVideo: (sprintId: string, video: GeneratedVideo) => void;
  updateBriefInSprint: (sprintId: string, briefId: string, patch: Partial<VideoBrief>) => void;
  deleteBriefFromSprint: (sprintId: string, briefId: string) => void;
  duplicateBriefInSprint: (sprintId: string, briefId: string) => string | null;
  setResearchResults: (r: ResearchResults | null) => void;
  setResearchStatus: (s: ResearchStatus) => void;
  setResearchError: (e: string | null) => void;
  setLastResearchAt: (ts: string | null) => void;
  hydrateResearch: (workspaceId: string) => void;
  hydrateLocal: (workspaceId: string) => void;
  reset: () => void;
}

const INITIAL = {
  workspaceId: null as string | null,
  selectedBrandId: null as string | null,
  selectedBrandConfig: null as BrandConfig | null,
  character: null as BrandCharacter | null,
  characterLoading: false,
  characterError: null as string | null,
  characterAnglePackGenerating: false,
  characterAnglePackError: null as string | null,
  brandIdentity: {} as Partial<BrandIdentityData>,
  characterGenJob: null as CharacterGenJob | null,
  selectedPortraitIndex: null as number | null,
  sprints: [] as Sprint[],
  researchResults: null as ResearchResults | null,
  researchStatus: 'idle' as ResearchStatus,
  researchError: null as string | null,
  lastResearchAt: null as string | null,
};

function researchCacheKey(wsId: string) {
  return `social-studio-research-${wsId}`;
}

function characterCacheKey(wsId: string) {
  return `social-studio-character-${wsId}`;
}

function brandIdentityCacheKey(wsId: string) {
  return `social-studio-brand-${wsId}`;
}

function sprintsCacheKey(wsId: string) {
  return `social-studio-sprints-${wsId}`;
}

export const useSocialStudioStore = create<SocialStudioState>((set, get) => ({
  ...INITIAL,

  setWorkspaceId: (id) => {
    // Clear per-workspace state when the active workspace changes so a
    // previously-locked character / sprints / brand identity from
    // workspace A can't leak into workspace B's UI before
    // ``hydrateLocal`` re-populates from B's localStorage. Without this,
    // navigating from a workspace with a locked character to a fresh
    // one shows the old character until the artifact-fetch round-trips
    // and clears it (or fails to clear it, if no character exists yet
    // for the new workspace — which is the leak the user just hit).
    const prev = get().workspaceId;
    if (prev && prev !== id) {
      set({
        workspaceId: id,
        // Brand selection is per-workspace — never carry it across.
        selectedBrandId: null,
        selectedBrandConfig: null,
        character: null,
        characterError: null,
        characterLoading: false,
        characterAnglePackGenerating: false,
        characterAnglePackError: null,
        characterGenJob: null,
        sprints: [],
        researchResults: null,
        researchStatus: 'idle',
        researchError: null,
        lastResearchAt: null,
        brandIdentity: INITIAL.brandIdentity,
      });
      return;
    }
    set({ workspaceId: id });
  },
  setSelectedBrandId: (brandId) => {
    // Clearing the cached config is intentional — SocialStudioWorkspace's
    // effect re-fetches whenever selectedBrandId changes and re-populates
    // selectedBrandConfig. Avoids a stale config leaking into brief /
    // research / calendar inputs between change → fetch.
    const prev = get().selectedBrandId;
    if (prev === brandId) return;
    set({ selectedBrandId: brandId, selectedBrandConfig: null });
  },
  setSelectedBrandConfig: (config) => set({ selectedBrandConfig: config }),
  setCharacter: (c) => {
    set({ character: c });
    const wsId = get().workspaceId;
    if (wsId) {
      try {
        if (c) localStorage.setItem(characterCacheKey(wsId), JSON.stringify(c));
        else localStorage.removeItem(characterCacheKey(wsId));
      } catch { /* ignore */ }
    }
  },
  setCharacterLoading: (l) => set({ characterLoading: l }),
  setCharacterError: (e) => set({ characterError: e }),
  setCharacterAnglePackGenerating: (g) => set({ characterAnglePackGenerating: g }),
  setCharacterAnglePackError: (e) => set({ characterAnglePackError: e }),
  updateBrandIdentity: (partial) => {
    set((st) => ({ brandIdentity: { ...st.brandIdentity, ...partial } }));
    const wsId = get().workspaceId;
    if (wsId) {
      try {
        localStorage.setItem(brandIdentityCacheKey(wsId), JSON.stringify(get().brandIdentity));
      } catch { /* ignore */ }
    }
  },
  setCharacterGenJob: (j) => set({ characterGenJob: j }),
  setSelectedPortraitIndex: (i) => set({ selectedPortraitIndex: i }),
  addSprint: (s) => {
    set((st) => ({ sprints: [s, ...st.sprints] }));
    const wsId = get().workspaceId;
    if (wsId) {
      try {
        localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints));
      } catch { /* ignore */ }
    }
  },
  renameSprint: (sprintId, label) => {
    set((st) => ({
      sprints: st.sprints.map((sp) => sp.id === sprintId ? { ...sp, label } : sp),
    }));
    const wsId = get().workspaceId;
    if (wsId) {
      try { localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints)); } catch { /* ignore */ }
    }
  },
  deleteSprint: (sprintId) => {
    set((st) => ({ sprints: st.sprints.filter((sp) => sp.id !== sprintId) }));
    const wsId = get().workspaceId;
    if (wsId) {
      try { localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints)); } catch { /* ignore */ }
    }
  },
  addGeneratedVideo: (sprintId, video) => {
    set((st) => ({
      sprints: st.sprints.map((sp) =>
        sp.id === sprintId
          ? { ...sp, generatedVideos: [...sp.generatedVideos, video] }
          : sp,
      ),
    }));
    const wsId = get().workspaceId;
    if (wsId) {
      try {
        localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints));
      } catch { /* ignore */ }
    }
  },

  updateBriefInSprint: (sprintId, briefId, patch) => {
    set((st) => ({
      sprints: st.sprints.map((sp) =>
        sp.id === sprintId
          ? { ...sp, briefs: sp.briefs.map((b) => (b.id === briefId || b.calendar_slot === briefId) ? { ...b, ...patch } : b) }
          : sp,
      ),
    }));
    const wsId = get().workspaceId;
    if (wsId) try { localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints)); } catch { /**/ }
  },

  deleteBriefFromSprint: (sprintId, briefId) => {
    set((st) => ({
      sprints: st.sprints.map((sp) =>
        sp.id === sprintId
          ? { ...sp, briefs: sp.briefs.filter((b) => b.id !== briefId && b.calendar_slot !== briefId) }
          : sp,
      ),
    }));
    const wsId = get().workspaceId;
    if (wsId) try { localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints)); } catch { /**/ }
  },

  duplicateBriefInSprint: (sprintId, briefId) => {
    const sprint = get().sprints.find((sp) => sp.id === sprintId);
    const original = sprint?.briefs.find((b) => b.id === briefId || b.calendar_slot === briefId);
    if (!original) return null;
    const newId = `${Date.now()}-copy`;
    const duplicate: VideoBrief = {
      ...original,
      id: newId,
      title: `${original.title} (copy)`,
      calendar_slot: `${original.calendar_slot || briefId}-copy`,
    };
    set((st) => ({
      sprints: st.sprints.map((sp) =>
        sp.id === sprintId
          ? { ...sp, briefs: [...sp.briefs, duplicate] }
          : sp,
      ),
    }));
    const wsId = get().workspaceId;
    if (wsId) try { localStorage.setItem(sprintsCacheKey(wsId), JSON.stringify(get().sprints)); } catch { /**/ }
    return newId;
  },

  setResearchResults: (r) => {
    set({ researchResults: r });
    // Persist to localStorage
    const wsId = get().workspaceId;
    if (wsId && r) {
      try {
        localStorage.setItem(researchCacheKey(wsId), JSON.stringify({
          results: r,
          lastResearchAt: get().lastResearchAt,
        }));
      } catch { /* quota exceeded — ignore */ }
    }
  },
  setResearchStatus: (s) => set({ researchStatus: s }),
  setResearchError: (e) => set({ researchError: e }),
  setLastResearchAt: (ts) => {
    set({ lastResearchAt: ts });
    const wsId = get().workspaceId;
    const r = get().researchResults;
    if (wsId && r) {
      try {
        localStorage.setItem(researchCacheKey(wsId), JSON.stringify({
          results: r,
          lastResearchAt: ts,
        }));
      } catch { /* quota exceeded — ignore */ }
    }
  },

  hydrateResearch: (workspaceId) => {
    try {
      const raw = localStorage.getItem(researchCacheKey(workspaceId));
      if (!raw) return;
      const { results, lastResearchAt } = JSON.parse(raw);
      if (results) {
        set({ researchResults: results, lastResearchAt, researchStatus: 'complete' });
      }
    } catch { /* corrupt data — ignore */ }
  },

  hydrateLocal: (workspaceId) => {
    // Sync hydration from localStorage — runs BEFORE any API fetch,
    // so the UI shows cached data immediately on page load.
    try {
      const charRaw = localStorage.getItem(characterCacheKey(workspaceId));
      if (charRaw) {
        const character = JSON.parse(charRaw) as BrandCharacter;
        if (character?.portraitUrl) set({ character });
      }
    } catch { /* ignore */ }

    try {
      const brandRaw = localStorage.getItem(brandIdentityCacheKey(workspaceId));
      if (brandRaw) {
        const brandIdentity = JSON.parse(brandRaw) as Partial<BrandIdentityData>;
        set({ brandIdentity });
      }
    } catch { /* ignore */ }

    try {
      const sprintsRaw = localStorage.getItem(sprintsCacheKey(workspaceId));
      if (sprintsRaw) {
        const sprints = JSON.parse(sprintsRaw) as Sprint[];
        if (Array.isArray(sprints) && sprints.length > 0) set({ sprints });
      }
    } catch { /* ignore */ }
  },

  reset: () => {
    const wsId = get().workspaceId;
    // Clear localStorage caches for this workspace
    if (wsId) {
      try {
        localStorage.removeItem(researchCacheKey(wsId));
        localStorage.removeItem(characterCacheKey(wsId));
        localStorage.removeItem(brandIdentityCacheKey(wsId));
        localStorage.removeItem(sprintsCacheKey(wsId));
      } catch { /* ignore */ }
    }
    // Reset state but preserve workspaceId so user stays in the same workspace
    set({ ...INITIAL, workspaceId: wsId });
  },
}));
